import { FeeType, MissionFee, RepetitionType } from "../__generated__/graphql";
import { StyleSheet, TextInput } from "react-native";
import { useTheme } from "../hooks/use-theme";
import Switch from "./Switch";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TextField from "../components/TextField";
import Gap from "./Gap";
import SelectField from "../components/SelectField";
import HStack from "./HStack";
import VStack from "./VStack";
import { BodyLargeText, BodyMediumText, BodySmallText } from "../components/FormattedText";
import { currentClientMissionFeesRate } from "../contexts/CreateMissionManager";
import { useRecoilValue } from "recoil";
import { useI18n } from "../hooks/i18n-hooks";
import { formatCurrency } from "../utils";
import ListItem from "./ListItem";
import Text from "../components/Text";
import { MarkOptional } from "ts-essentials";

export type FeeData = MarkOptional<
    Pick<MissionFee, "id" | "type" | "repetition" | "otherTitle" | "externalId">,
    "id" | "type" | "repetition"
> & {
    amount?: number;
    isEnabled: boolean;
};

type ActivableListItemProps = {
    title: string;
    description?: string;
    repetitionsOptions: { label: string; value: RepetitionType }[];
    onChange: (value: Partial<FeeData>) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    value: FeeData;
    maxValue?: number;
};

const ActivableListItem = ({
    title,
    description,
    repetitionsOptions,
    onChange,
    onFocus,
    onBlur,
    value,
    maxValue = 9999,
}: ActivableListItemProps) => {
    const isEnabled = !!value?.isEnabled;
    const isOther = useMemo(() => value.type === FeeType.Other, [value]);
    const i18n = useI18n();
    const styles = useStyles();
    const { colors, marginSize } = useTheme();
    const [isOtherTitleFocused, setIsOtherTitleFocused] = useState(isOther);
    const [isValueFocused, setIsValueFocused] = useState(false);
    const client_mission_fees_rate = useRecoilValue(currentClientMissionFeesRate);
    const [amountInternalValue, setAmountInternalValue] = useState<string>(
        value.amount ? value.amount.toString() : "",
    );
    const hasSingleValue = useMemo(
        () => repetitionsOptions.length === 1 && repetitionsOptions[0].value == value.repetition,
        [repetitionsOptions, value.repetition],
    );

    const valueInputRef = useRef<TextInput>(null);

    const onSetAmountValue = useCallback(
        (newAmount: string) => {
            // we should develop a MoneyInput component to handle this case properly
            if (!newAmount.endsWith(".") && !newAmount.endsWith(",")) {
                const parsedValue = parseFloat(newAmount);
                onChange({ amount: !isNaN(parsedValue) ? parsedValue : undefined });
            }
            setAmountInternalValue((oldValue) => {
                if (newAmount != oldValue) {
                    return newAmount;
                }
                return oldValue;
            });
        },
        [onChange],
    );

    useEffect(() => {
        setAmountInternalValue((oldValue) => {
            if (value.amount?.toString() != oldValue) {
                return value.amount?.toString() || "";
            }
            return oldValue;
        });
    }, [value]);

    return (
        <VStack gap={1 / 2}>
            {(!value.type || !isOther) && (
                <HStack>
                    <Switch
                        style={{ paddingHorizontal: 0, paddingVertical: 0 }}
                        value={isEnabled}
                        onChange={(newValue) => {
                            onChange({ isEnabled: newValue });
                            if (newValue && !isOther) {
                                setTimeout(() => {
                                    valueInputRef.current?.focus();
                                }, 10); // 10 ms delay is required on Android to correctly focus the TextField
                            }
                        }}
                    />
                    <Gap gap="2/3" />
                    <BodyLargeText>{title}</BodyLargeText>
                </HStack>
            )}
            {isEnabled && value.type && (
                <VStack>
                    {isOther && (
                        <TextField
                            value={value.otherTitle || ""}
                            onChangeText={(newValue) => {
                                onChange({ otherTitle: newValue });
                            }}
                            label={i18n.t(`screens.user.create_mission_fees.fees_type`)}
                            autoFocus={!value.otherTitle}
                            error={!isOtherTitleFocused && !value.otherTitle}
                            showErrorText={false}
                            returnKeyType="next"
                            onFocus={() => {
                                setIsOtherTitleFocused(true);
                            }}
                            onBlur={() => {
                                setIsOtherTitleFocused(false);
                            }}
                            onSubmitEditing={() => {
                                valueInputRef.current?.focus();
                            }}
                        />
                    )}
                    <HStack>
                        <TextField
                            ref={valueInputRef}
                            error={
                                ((!isOther && !isValueFocused) ||
                                    (isOther && !isOtherTitleFocused && !isValueFocused)) &&
                                !value.amount
                            }
                            showErrorText={false}
                            maxValue={maxValue}
                            selectTextOnFocus
                            style={styles.amountField}
                            value={amountInternalValue}
                            onFocus={() => {
                                setIsValueFocused(true);
                                onFocus?.();
                            }}
                            onBlur={() => {
                                setIsValueFocused(false);
                                onBlur?.();
                            }}
                            returnKeyType="done"
                            textAlign="right"
                            left="CHF"
                            isDecimalNumber
                            onChangeText={onSetAmountValue}
                        />
                        <Gap gap={0.5} />
                        {hasSingleValue ? (
                            <BodyMediumText>{repetitionsOptions[0].label}</BodyMediumText>
                        ) : (
                            <SelectField
                                style={styles.repetitionField}
                                value={value.repetition}
                                data={repetitionsOptions}
                                halfScreen
                                onSelect={(newRepetition) => {
                                    onChange({
                                        repetition: newRepetition.value,
                                    });
                                }}
                            />
                        )}
                    </HStack>
                    <Gap gap={0.5} />
                    {!isOther ? (
                        <BodySmallText>
                            {i18n.t("screens.user.create_mission_fees.total_with_charge", {
                                amount: value.amount
                                    ? formatCurrency({
                                          amountInCents:
                                              (value.amount || 0) *
                                              (100 + Number(client_mission_fees_rate)),
                                      })
                                    : "--",
                            })}
                        </BodySmallText>
                    ) : (
                        <ListItem
                            description={i18n.t(
                                "screens.user.create_mission_fees.delete_other_fees",
                            )}
                            rightIcon="trash"
                            rightActionMode="medium"
                            style={{ marginHorizontal: -marginSize }}
                            rightIconColor={colors.error}
                            globalTextColor={colors.error}
                            onPress={() => {
                                onChange({ isEnabled: false });
                            }}
                        />
                    )}
                    {description && (
                        <BodySmallText color={colors.fieldLabel}>{description}</BodySmallText>
                    )}
                </VStack>
            )}
        </VStack>
    );
};

const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                amountField: {
                    textAlign: "right",
                    flexGrow: 0,
                    flexShrink: 0,
                    width: 135,
                },
                repetitionField: {
                    flexGrow: 1,
                    width: 100, // need to set a width for layaut to work. but flexGrow will extend it to fill the space
                },
            }),
        [],
    );
};

export default ActivableListItem;
